import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { isNil, omitBy } from 'lodash';
import { DateTime } from 'luxon';
import { useQuery } from '@tanstack/react-query';
import { Post, PostScore } from 'lib/types';
import { TagType } from 'lib/types/tags';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { useViewPostEvent } from 'lib/hooks/useEventTracking';
import { NETWORK_IDS, POST_TYPES } from 'lib/constants';
import { userIsProOrAdmin, userIsPaidTeamMember } from 'lib/helpers';
import { useUpgradeModal } from 'components/UpgradeModal';
import { PostEmbed } from 'components/PostEmbed';
import { PostCredits } from 'components/PostCredits';
import { CreditReport } from 'components/CreditReport';
import { AwaitUserAuth } from 'components/AwaitUserAuth';
import { PostInfo } from './PostInfo';
import { PostTags } from './PostTags';
import { PostLog } from './PostLog';
import { PostMatches } from './PostMatches';
import { PostConcepts } from './PostConcepts';
import { PostProjects } from './PostProjects';

interface Props {
  serverSidePost: Post;
  tagTypes: TagType[];
}

export const PostDetail = ({
  serverSidePost, tagTypes,
}: Props) => {
  const API = useApi();
  const currentUser = useCurrentUser();
  const { showUpgradeModal } = useUpgradeModal();
  const { enqueueSnackbar } = useSnackbar();

  const [post, setPost] = useState<Post>(serverSidePost);
  const [hasHiddenCredit, setHasHiddenCredit] = useState<boolean>();

  const { data: postScore } = useQuery<PostScore | undefined>({
    queryKey: ['getPostScore', post.id],
    queryFn: () => API.getPostScore(post.id),
    enabled: !!post.id,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });

  const refresh = async (message?: string) => {
    const p = await API.getPostById(post.id);
    setPost(p);
    if (message) {
      enqueueSnackbar(message, { variant: 'success' });
    }
  };

  useEffect(() => {
    // When the serverSidePost changes, see if it differs from the post in state.
    // If so, update post.
    if (serverSidePost?.id !== post?.id) {
      setPost(serverSidePost);
    }

    autoScrapePostIfNeeded();
  }, [serverSidePost?.id, post?.id]);

  useEffect(() => {
    const hasHidden = post.credits?.some((c) => c.isHidden);
    setHasHiddenCredit(hasHidden);
  }, [post]);

  // Tracks views of post
  useViewPostEvent(post.id);

  const [isScraping, setIsScraping] = useState(false);
  const manualScrapePost = async () => {
    if (!userIsProOrAdmin(currentUser)) {
      showUpgradeModal('Upgrade to fetch new stats anytime you want');
      return;
    }

    if (!isScraping) {
      enqueueSnackbar('Fetching stats for post...', {
        variant: 'info',
      });

      setIsScraping(true);
      const response = await API.scrapePostMetrics(post.id);
      if (response?.delta) {
        refresh();
        const filteredDelta = omitBy(response?.delta, isNil);
        const newStats = Object.entries(filteredDelta).map(([key, value]) => `${key} ${value >= 0 ? '+' : ''}${value}`).join(', ');
        enqueueSnackbar(`Stats fetched successfully: ${newStats}`, {
          variant: 'info',
        });
      } else {
        enqueueSnackbar('Fetching failed, try again later.', { variant: 'error' });
      }
      setIsScraping(false);
    }
  };

  /**
   * When a user opens a post detail page, we trigger a stats refresh on the post
   * in certain cases. We do this discreetly, and update the stats silently.
   */
  const autoScrapePostIfNeeded = async () => {
    const twelveHoursAgo = DateTime.now().minus({ hours: 12 }).toSQL();
    const thirtyMinutesAgo = DateTime.now().minus({ minutes: 30 }).toSQL();
    const twoWeeksAgo = DateTime.now().minus({ weeks: 2 }).toSQL();

    if (
      (!post.thumbnailUrl && post.type !== POST_TYPES.TEXT)
      || (post.type === POST_TYPES.VIDEO && !post.currentViews)
      || (post.provisional && post.network !== NETWORK_IDS.facebook)) {
      // If the post has no thumbnail or is a video with no views
      // or is set as provisional, we need to scrape it.
      try {
        await API.queueScrapePost(post.id);
      } catch (err) {
        // noop
      }
      return;
    }

    if (post.network === NETWORK_IDS.facebook) {
      // We never auto-scrape Facebook or Twitter posts.
      return;
    }

    if (post.postedAt < twoWeeksAgo && post.statsUpdatedAt > twelveHoursAgo) {
      // If the post is 2+ weeks old and its stats have been updated in the past 12 hours,
      // no need to refresh.
      return;
    }

    if (post.postedAt > twoWeeksAgo && post.statsUpdatedAt > thirtyMinutesAgo) {
      // If the post is less than 2 weeks old, but the stats were updated in the past 30 minutes,
      // no need to refresh.
      return;
    }
    try {
      await API.queueScrapePost(post.id);
    } catch (err) {
      // noop
    }
  };

  return (
    <div className="max-w-screen-2xl m-auto p-6">
      <div className="grid md:grid-row-2 md:grid-cols-2 md:grid-flow-row gap-6">
        <div className="row-span-1 min-h-[480px] mx-auto w-full max-w-xs sm:max-w-md">
          <PostEmbed post={post} />
        </div>
        <div className="row-span-2">
          <PostInfo
            post={post}
            onChange={refresh}
            onScrapePost={manualScrapePost}
            isScraping={isScraping}
            postScore={postScore}
          />
          <PostCredits
            post={post}
            onChange={refresh}
          />
          <CreditReport
            post={post}
            onChange={refresh}
          />
          <h6 className="text-lg font-semibold mb-2">Tags, Events, and Projects</h6>
          <PostTags
            post={post}
            tagTypes={tagTypes}
            onChange={refresh}
          />
          <AwaitUserAuth>
            <PostProjects post={post} />
            <PostMatches post={post} onChange={refresh} />
            {userIsPaidTeamMember(currentUser) && <PostConcepts post={post} />}
          </AwaitUserAuth>
        </div>
        <div className="row-span-1">
          {!hasHiddenCredit && <PostLog post={post} />}
        </div>
      </div>
    </div>
  );
};
